import { useCallback, useEffect, useRef, useState } from "react";
import { i18n, initializeI18n } from "../localization/i18n";
import { translate } from "../localization/translateService";
import unlockPotential from "../stories/assets/unlock-potential.svg";

// "Achieve your goals in 4 steps" — interactive steps on the left; the right panel
// shows the ACTIVE step's image. CMS-driven: title / highlight / subtitle +
// `steps` ({ title, description, image }). stepsImage is a global fallback.
// Localization: when CMS field is empty -> i18n translate() defaults (no hardcoded English in markup).

export default function TutoringSection({ props }) {
  const [lang, setLang] = useState(null);
  const [t, setT] = useState({});

  // Init i18n + track current language (re-translate on language change)
  useEffect(() => {
    if (!i18n.isInitialized) {
      initializeI18n().then(() => setLang(i18n.language));
    } else {
      setLang(i18n.language);
    }
  }, []);

  // i18n defaults — used only when the CMS field is empty (no hardcoded English in markup)
  useEffect(() => {
    let active = true;
    (async () => {
      const out = {};
      out.title = await translate("UMBRACO_LANDING.HOME.STEPS.TITLE", false, "Achieve your goals in");
      out.highlight = await translate("UMBRACO_LANDING.HOME.STEPS.HIGHLIGHT", false, "4 steps");
      out.subtitle = await translate("UMBRACO_LANDING.HOME.STEPS.SUBTITLE", false, "Driven by DOTORE's own dedicated management system — Korea's representative education platform");
      out.stepLabel = await translate("UMBRACO_LANDING.HOME.STEPS.STEP_LABEL", false, "Step");
      out.sticker = await translate("UMBRACO_LANDING.HOME.STEPS.STICKER", false, "Unlock your potential");
      out.step1Title = await translate("UMBRACO_LANDING.HOME.STEPS.STEP1_TITLE", false, "Level Test");
      out.step1Desc = await translate("UMBRACO_LANDING.HOME.STEPS.STEP1_DESC", false, "Know exactly where you stand with a quick, free placement test.");
      out.step2Title = await translate("UMBRACO_LANDING.HOME.STEPS.STEP2_TITLE", false, "Tailored Course Recommendation");
      out.step2Desc = await translate("UMBRACO_LANDING.HOME.STEPS.STEP2_DESC", false, "Get classes handpicked for your level, goal, and pace by a real advisor.");
      out.step3Title = await translate("UMBRACO_LANDING.HOME.STEPS.STEP3_TITLE", false, "Live Class & 1:1 Management");
      out.step3Desc = await translate("UMBRACO_LANDING.HOME.STEPS.STEP3_DESC", false, "Join 1:N group or 1:1 live classes and stay on track with your personal tutor's feedback.");
      out.step4Title = await translate("UMBRACO_LANDING.HOME.STEPS.STEP4_TITLE", false, "Reach Your Goal");
      out.step4Desc = await translate("UMBRACO_LANDING.HOME.STEPS.STEP4_DESC", false, "Pass TOPIK, study abroad, or land the job — with someone guiding you the whole way.");
      if (active) setT(out);
    })();
    return () => { active = false; };
  }, [lang]);

  const defaultSteps = [
    { title: t.step1Title, description: t.step1Desc },
    { title: t.step2Title, description: t.step2Desc },
    { title: t.step3Title, description: t.step3Desc },
    { title: t.step4Title, description: t.step4Desc },
  ];

  const title = t.title || props?.stepsTitle || props?.tutoringSectionTitle1 || "";
  const highlight = t.highlight || props?.stepsHighlight || "";
  const subtitle =
    t.subtitle ||
    props?.stepsSubtitle ||
    props?.tutoringSectionTitle2 ||
    "";
  const fallbackImage = props?.stepsImage;
  // Per-step data from the CMS "steps" Block List ({ title, description, image }).
  // Each step shows its OWN image; i18n defaults fill text when a block is empty,
  // and the global stepsImage is only a last-resort fallback when a step has no image.
  const cmsSteps = (Array.isArray(props?.steps) ? props.steps : []).filter(Boolean);
  const steps = defaultSteps.map((s, i) => {
    const c = cmsSteps[i] || {};
    return {
      title: c.title || s.title,
      description: c.description || s.description,
      image: c.image || fallbackImage,
    };
  });

  const [active, setActive] = useState(0);
  const activeImage = steps[active]?.image || fallbackImage;

  // Scroll-jacking: the section is made tall (inline height) and its content pins
  // (CSS position:sticky). The active step (01–04) is derived from how far the user
  // has scrolled THROUGH the section -> bidirectional (scroll up decrements). The pin
  // releases naturally once the tall section has scrolled past. Disabled on mobile
  // (total <= 0 when the section isn't taller than the viewport).
  const stepRefs = useRef([]);
  const sectionRef = useRef(null);
  const markerRef = useRef(null);
  const activeRef = useRef(0);
  activeRef.current = active;

  // Lock-to-step highlight: scroll picks a DISCRETE step (round of progress) and the
  // marker is placed exactly on that step's box — same offsetTop/offsetHeight — with no
  // CSS transition, so it snaps cleanly and is ALWAYS locked on one step, never floating
  // between them. Off the pin (mobile/static) it sits on the active (clicked) step.
  const positionMarker = useCallback(() => {
    const el = sectionRef.current;
    const marker = markerRef.current;
    if (!el || !marker || !steps.length) return;
    let idx = activeRef.current;
    const total = el.offsetHeight - window.innerHeight; // scrollable range while pinned
    if (total > 0) {
      const scrolled = Math.min(Math.max(-el.getBoundingClientRect().top, 0), total);
      const progress = scrolled / total;               // 0..1
      idx = Math.min(steps.length - 1, Math.max(0, Math.round(progress * (steps.length - 1))));
      setActive((prev) => (prev !== idx ? idx : prev));
    }
    const step = stepRefs.current[idx];
    if (step) {
      marker.style.transform = `translateY(${step.offsetTop}px)`;
      marker.style.height = `${step.offsetHeight}px`;   // each step's own height -> exact lock
    }
  }, [steps.length]);

  useEffect(() => {
    let raf = 0;
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; positionMarker(); }); };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    positionMarker();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [positionMarker]);

  // Reposition on active change off-pin (mobile tap) + after text/layout settles.
  useEffect(() => { positionMarker(); }, [active, t, positionMarker]);

  const [carouselActive, setCarouselActive] = useState(0);
  const carouselRef = useRef(null);
  const onCarouselScroll = useCallback(() => {
    const el = carouselRef.current;
    if (!el || !el.children.length) return;
    const center = el.scrollLeft + el.clientWidth / 2;
    let idx = 0;
    let best = Infinity;
    Array.from(el.children).forEach((c, i) => {
      const mid = c.offsetLeft + c.offsetWidth / 2;
      const d = Math.abs(mid - center);
      if (d < best) { best = d; idx = i; }
    });
    setCarouselActive(idx);
  }, []);

  return (
    <section
      className="steps-section"
      ref={sectionRef}
      style={{ height: `calc(100vh + ${steps.length * 14}vh)` }}
    >
      <div className="steps-section__pin">
      <div className="steps-section__head">
        <h2 className="steps-section__title animate-on-scroll">
          {title}{" "}
          <span className="steps-section__hl">
            <svg className="steps-section__swoosh" viewBox="0 0 142.597 76.7048" fill="none" aria-hidden="true">
              <path d="M4.30729 54.9466C26.0818 32.4929 96.5227 21.6152 74.7925 68.425C72.0256 74.3851 49.3493 68.4056 56.719 53.4509C64.0888 38.4962 94.9664 1.59362 141.982 6.43507" strokeWidth="12" />
            </svg>
            <span className="steps-section__hl-text">{highlight}</span>
          </span>
        </h2>
        <p className="steps-section__subtitle animate-on-scroll">{subtitle}</p>
      </div>

      <div className="steps-section__body">
        <ol className="steps-list">
          <span className="steps-list__hl" ref={markerRef} aria-hidden="true" />
          {steps.map((s, i) => (
            <li
              className={"step-item animate-on-scroll" + (i === active ? " step-item--active" : "")}
              key={i}
              ref={(el) => (stepRefs.current[i] = el)}
              data-idx={i}
              role="button"
              tabIndex={0}
              aria-pressed={i === active}
              onClick={() => setActive(i)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setActive(i); } }}
            >
              <span className="step-item__no">{t.stepLabel} {String(i + 1).padStart(2, "0")}</span>
              <h3 className="step-item__title">{s.title}</h3>
              {s.description && <p className="step-item__desc">{s.description}</p>}
            </li>
          ))}
        </ol>

        <div className="steps-section__media steps-section__media--sticker">
          {/* Render ALL step images stacked + toggle by opacity, so they load once
              up-front and switching steps is instant (no per-swap fetch/decode freeze). */}
          {steps.some((s) => s.image || fallbackImage) ? (
            steps.map((s, i) => {
              const src = s.image || fallbackImage;
              return src ? (
                <img
                  key={i}
                  src={src}
                  alt=""
                  className={"steps-section__media-img" + (i === active ? " is-active" : "")}
                  decoding="async"
                  aria-hidden={i !== active}
                />
              ) : null;
            })
          ) : (
            <div className="steps-section__media-ph" aria-hidden="true">
              <svg viewBox="0 0 48 48" width="56" height="56">
                <rect x="6" y="9" width="36" height="30" rx="3" fill="none" stroke="currentColor" strokeWidth="2" />
                <circle cx="17" cy="19" r="3.5" fill="currentColor" />
                <path d="M10 36l9-10 7 7 6-6 6 7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
              </svg>
            </div>
          )}
          <img className="steps-section__sticker" src={unlockPotential} alt={t.sticker} aria-hidden="true" />
        </div>
      </div>

      <div className="steps-mobile">
        <img className="steps-mobile__sticker" src={unlockPotential} alt={t.sticker} aria-hidden="true" />
        <div className="steps-mobile__track" ref={carouselRef} onScroll={onCarouselScroll}>
          {steps.map((s, i) => {
            const src = s.image || fallbackImage;
            return (
              <div className="steps-mobile__card" key={i}>
                {src && <img className="steps-mobile__img" src={src} alt="" decoding="async" />}
                <div className="steps-mobile__text">
                  <span className="steps-mobile__no">{t.stepLabel} {String(i + 1).padStart(2, "0")}</span>
                  <h3 className="steps-mobile__title">{s.title}</h3>
                  {s.description && <p className="steps-mobile__desc">{s.description}</p>}
                </div>
              </div>
            );
          })}
        </div>
        <div className="steps-mobile__dots" aria-hidden="true">
          {steps.map((_, i) => (
            <span key={i} className={"steps-mobile__dot" + (i === carouselActive ? " is-active" : "")} />
          ))}
        </div>
      </div>
      </div>
    </section>
  );
}
